import { Redirect, router } from "expo-router";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useState } from "react";
import { Platform, StyleSheet } from "react-native";
import { z } from "zod";
import { Button } from "~/components/Button";
import { Icon } from "~/components/Icon";
import { IconButton } from "~/components/IconButton";
import { IconButtonWithBadge } from "~/components/IconButtonWithBadge";
import { Pressable } from "~/components/Pressable";
import { Screen } from "~/components/Screen";
import { Spacer } from "~/components/Spacer";
import { Spinner } from "~/components/Spinner";
import { Text } from "~/components/Text";
import { View } from "~/components/View";
import { useAlert } from "~/hooks/useAlert";
import { state } from "~/store";
import { styleConstants } from "~/style/styleConstants";
import { assert } from "~/utils/assert";
import { assertSchema } from "~/utils/assertSchema";
import { delay } from "~/utils/delay";
import { FormattedMessage, getLocaleURL } from "../intl";
import { RequestCameraPermissionView } from "./RequestCameraPermissionView";
import { getAudioOptions } from "./getAudioOptions";
import { useResponsive } from "./useResponsive";
import { userDetailsSchema } from "./userDetailsSchema";
import { RTCVideo, mediaDevices } from "./web-rtc";
import type { MediaStream } from "./web-rtc";
import type { AlertButton } from "react-native";

function GetPermissions({ children }: { children: React.ReactNode }) {
  if (Platform.OS === "web") {
    return <>{children}</>;
  } else {
    return (
      <RequestCameraPermissionView showBackButton={false} isOnVideoChat>
        {children}
      </RequestCameraPermissionView>
    );
  }
}

export const LiveVideoSettingsScreen = () => {
  const userDetails = {
    name: state.localUserDetails.name,
    age: state.localUserDetails.age,
    sex: state.localUserDetails.sex,
  };

  if (userDetailsSchema.isValidSync(userDetails) === false) {
    console.log("No user details found in store", userDetails);
    return <Redirect href={getLocaleURL("/")} />;
  }

  return <LiveVideoSettingsScreenValidated />;
};

const LiveVideoSettingsScreenValidated = () => {
  return (
    <Screen withBottomInset withTopInset={false} preventScroll>
      <GetPermissions>
        <LiveVideoSettingsInner />
      </GetPermissions>
    </Screen>
  );
};

const LiveVideoSettingsInner = observer(function LiveVideoSettingsInner() {
  const alert = useAlert();

  const [isLocalMicOn, setIsLocalMicOn] = useState(true);

  const [preferredAudioOutput, setPreferredAudioOutput] = useState<
    "BLUETOOTH" | "WIRED_HEADSET" | "SPEAKER" | undefined
  >(undefined);

  const [userMediaWithDimensions, setUserMediaWithDimensions] =
    useState<null | {
      userMedia: MediaStream;
      dimensions: { width: number; height: number };
    }>(null);

  const facingMode: "user" | "environment" =
    userMediaWithDimensions?.userMedia
      .getVideoTracks()
      // @ts-expect-error This is reported as TS error, but exists on mobile
      .at(0)?.__facingMode ?? "user";

  // We use this in a couple of places, so we put it in a function
  const getVideoDimensionsAndSetUserMedia = useCallback(
    function getVideoDimensionsAndSetUserMedia(userMedia: MediaStream) {
      const videoTrack = userMedia.getVideoTracks()[0];

      if (!videoTrack) {
        alert("No video input devices found");
        router.replace(getLocaleURL("/"));
        return;
      }

      // Expected result: {"frameRate": 30, "height": 720, "width": 1280}
      const videoSettings = videoTrack.getSettings();

      assertSchema(
        videoSettings,
        z.object({
          width: z.number(),
          height: z.number(),
          frameRate: z.number(),
        }),
      );

      const localStreamDimensions =
        Platform.OS === "web"
          ? {
              width: videoSettings.width,
              height: videoSettings.height,
            }
          : {
              // For some reason sometimes width is height and height is width
              // so we take the min of the two for the width and
              // the max of the two for the height
              width: Math.min(videoSettings.width, videoSettings.height),
              height: Math.max(videoSettings.width, videoSettings.height),
            };

      setUserMediaWithDimensions({
        userMedia,
        dimensions: localStreamDimensions,
      });
    },
    [alert],
  );

  const [selectedVideoInputDeviceId, setSelectedVideoInputDeviceId] = useState<
    string | undefined
  >(undefined);
  const [selectedAudioInputDeviceId, setSelectedAudioInputDeviceId] = useState<
    string | undefined
  >(undefined);
  const [videoInputDeviceList, setVideoInputDeviceList] = useState<
    MediaDeviceInfo[]
  >([]);
  const [audioInputDeviceList, setAudioInputDeviceList] = useState<
    MediaDeviceInfo[]
  >([]);

  const selectedVideoInputDevice = videoInputDeviceList.find(
    (device) => device.deviceId === selectedVideoInputDeviceId,
  );
  const selectedAudioInputDevice = audioInputDeviceList.find(
    (device) => device.deviceId === selectedAudioInputDeviceId,
  );

  useEffect(
    function initializeUserMediaEffect() {
      // Here we keep a list od cleanup functions we use to clean up the effect
      // In the effect cleanup we go through all of them and call them
      const cleanupList: (() => void)[] = [];

      async function runInitializeUserMediaEffect() {
        // Calling this requests camera permission.
        // The response is a media stream containing the default user facing media
        // We later use this to find the default audio and video tracks in the list
        // returned by enumerateDevices().
        const defaultUserFacingMedia = await mediaDevices.getUserMedia({
          audio: true,
          video: { facingMode: "user" },
        });

        const defaultTrackList = defaultUserFacingMedia.getTracks();
        for (const track of defaultTrackList) {
          track.stop();
          defaultUserFacingMedia.removeTrack?.(track); // Added on web
        }
        defaultUserFacingMedia.release?.(true);

        const deviceList =
          (await mediaDevices.enumerateDevices()) as MediaDeviceInfo[];

        const videoInputDeviceList = deviceList.filter(
          (device) => device.kind === "videoinput",
        );

        const audioInputDeviceList = deviceList.filter(
          (device) => device.kind === "audioinput",
        );

        if (videoInputDeviceList.length === 0) {
          alert("No video input devices found");
          router.replace(getLocaleURL("/"));
          return;
        }

        setVideoInputDeviceList(videoInputDeviceList);
        setAudioInputDeviceList(audioInputDeviceList);

        function getDefaultAudioDeviceId() {
          // Here we get the audio track returned in getUserMedia()
          const defaultAudioTrack = defaultUserFacingMedia
            .getAudioTracks()
            .at(0);

          // If we found a default audio track, we use it to try and find the device
          // first by id, then by label. This is needed because there are differences
          // in how web and mobile implementations work.
          if (defaultAudioTrack) {
            const matchById = audioInputDeviceList.find((d) => {
              return d.deviceId === defaultAudioTrack.id;
            });

            if (matchById) {
              return matchById.deviceId;
            }

            const matchByLabel = audioInputDeviceList.find((d) => {
              return d.label === defaultAudioTrack.label;
            });

            if (matchByLabel) {
              return matchByLabel.deviceId;
            }
          }

          const audioDeviceId = audioInputDeviceList[0]?.deviceId;
          assert(!!audioDeviceId, "No audioDeviceId");
          return audioDeviceId;
        }

        const audioDeviceId = getDefaultAudioDeviceId();
        setSelectedAudioInputDeviceId(audioDeviceId);

        function getDefaultVideoDeviceId() {
          // Here we get the video track returned in getUserMedia()
          const defaultVideoTrack = defaultUserFacingMedia
            .getVideoTracks()
            .at(0);

          // If we found a default video track, we use it to try and find the device
          // first by id, then by label. This is needed because there are differences
          // in how web and mobile implementations work.
          if (defaultVideoTrack) {
            const matchById = videoInputDeviceList.find((d) => {
              return d.deviceId === defaultVideoTrack.id;
            });

            if (matchById) {
              console.log("Got device by id");

              return matchById.deviceId;
            }

            const matchByLabel = videoInputDeviceList.find((d) => {
              return d.label === defaultVideoTrack.label;
            });

            if (matchByLabel) {
              console.log("Got device by label");

              return matchByLabel.deviceId;
            }
          }

          const facingFront = videoInputDeviceList.find((d) => {
            // @ts-expect-error This is reported as TS error, but exists on mobile
            return d.facing === "front";
          });

          if (facingFront) {
            console.log("Got device by front");

            return facingFront.deviceId;
          }

          console.log("Fall back to default");

          const videoDeviceId = videoInputDeviceList[0]?.deviceId;
          assert(!!videoDeviceId, "No videoDeviceId");
          return videoDeviceId;
        }

        const videoDeviceId = getDefaultVideoDeviceId();
        assert(videoDeviceId != null, "No videoDeviceId");
        setSelectedVideoInputDeviceId(videoDeviceId);

        // Now we getUserMedia again, but this time with the selected devices by id.
        const userMedia = await mediaDevices.getUserMedia({
          ...(!audioDeviceId
            ? undefined
            : { audio: { deviceId: audioDeviceId } }),
          video: { deviceId: videoDeviceId },
        });

        const trackList = userMedia.getTracks();

        cleanupList.push(() => {
          for (const track of trackList) {
            track.stop();
            userMedia.removeTrack?.(track); // Added on web
          }
          userMedia.release?.(true);
        });

        for (const track of userMedia.getVideoTracks()) {
          // @ts-expect-error This is reported as TS error, but exists on mobile
          track.__facingMode = track.getSettings().facingMode ?? "user";
        }

        getVideoDimensionsAndSetUserMedia(userMedia);
      }

      runInitializeUserMediaEffect();

      return () => {
        for (const cleanup of cleanupList) {
          cleanup();
        }
      };
    },
    [alert, getVideoDimensionsAndSetUserMedia],
  );

  const r = useResponsive();

  async function pickMicrophone() {
    const deviceList: MediaDeviceInfo[] =
      (await mediaDevices.enumerateDevices()) as any;

    const audioDeviceList = deviceList.filter(
      (device) => device.kind === "audioinput",
    );

    if (audioDeviceList.length === 0) {
      return;
    }

    const device = await new Promise<MediaDeviceInfo | null>((resolve) => {
      alert(
        "Microphone",
        undefined,
        audioDeviceList.map((d) => {
          return { text: d.label, onPress: () => resolve(d) };
        }),
        undefined,
        { cancelable: true, onDismiss: () => resolve(null) },
      );
    });

    if (!device) return;

    setSelectedAudioInputDeviceId(device.deviceId);

    const userMedia = await mediaDevices.getUserMedia({
      ...(!selectedVideoInputDeviceId
        ? undefined
        : { video: { deviceId: selectedVideoInputDeviceId } }),
      audio: { deviceId: device.deviceId },
    });

    getVideoDimensionsAndSetUserMedia(userMedia);
  }

  return (
    <View
      paddingExtraLarge
      flex
      style={{
        maxWidth: 1320,
        alignSelf: "center",
        width: "100%",
      }}
    >
      <Text sizeExtraLarge weightExtraBold alignCenter>
        <FormattedMessage
          id="KzBDfb"
          defaultMessage="Check your settings"
          description="Title on the live video settings screen where users can adjust their camera settings"
        />
      </Text>
      <Spacer medium />
      <Text sizeMedium alignCenter>
        <FormattedMessage
          id="eUmmfa"
          defaultMessage="Confirm your camera and microphone are active. When ready, press 'Let's Go!' to start."
          description="Description on the live video settings screen explaining how to start"
        />
      </Text>
      <Spacer medium />

      <View
        flex
        centerContent
        colorBackgroundLight={r(true, true, false)}
        borderRadiusMedium
        style={{ overflow: "hidden" }}
      >
        <View
          alignSelfCenter
          pointerEvents="none"
          style={{
            maxWidth: 600,
            maxHeight: 600 * (16 / 9),
            width: "100%",
            height: "100%",
          }}
        >
          {!userMediaWithDimensions ? (
            <View flex centerContent>
              <Spinner />
            </View>
          ) : (
            <RTCVideo
              mirror={facingMode === "user"}
              muted
              zOrder={1}
              objectFit="cover"
              style={style.localStream}
              stream={userMediaWithDimensions.userMedia}
            />
          )}
        </View>
        {userMediaWithDimensions && Platform.OS !== "web" && (
          <View
            absoluteBottomLeftLarge
            absoluteBottomRightLarge
            flexDirectionRow
            centerContent
            style={{ gap: styleConstants.spacingLarge, zIndex: 1000 }}
          >
            <View>
              <IconButton
                iconName="camera-flip"
                iconSize={24}
                iconColor={styleConstants.colorTextLight}
                style={style.iconContainer}
                disabled={videoInputDeviceList.length <= 1}
                onPress={async function switchCameraOnMobile() {
                  userMediaWithDimensions.userMedia
                    .getVideoTracks()
                    .forEach((track) => {
                      if (track.kind === "video") {
                        track._switchCamera();
                        // When we switch the camera, we need to update the facingMode manually.
                        // @ts-expect-error This is reported as TS error, but exists on mobile
                        track.__facingMode =
                          // @ts-expect-error This is reported as TS error, but exists on mobile
                          track.__facingMode === "user"
                            ? "environment"
                            : "user";
                      }
                    });
                  getVideoDimensionsAndSetUserMedia(
                    userMediaWithDimensions.userMedia,
                  );

                  // When the user taps the button too fast, the camera switches only once
                  // but the cameraFacingMode state is updated twice. This delay is to prevent that.
                  await delay(600);
                }}
              />
            </View>

            {/* #region Toggle mic on/of button */}
            <View>
              <IconButtonWithBadge
                shouldShowBadge
                badgeStyle={[
                  style.micBadge,
                  {
                    backgroundColor: isLocalMicOn
                      ? styleConstants.colorTextSuccess
                      : styleConstants.colorTextDanger,
                  },
                ]}
                iconName={isLocalMicOn ? "mic-on" : "mic-off"}
                iconSize={24}
                iconColor={styleConstants.colorTextLight}
                style={style.iconContainer}
                onPress={function toggleMic() {
                  const nextValue = !isLocalMicOn;
                  setIsLocalMicOn(nextValue);

                  for (const track of userMediaWithDimensions.userMedia.getAudioTracks()) {
                    track.enabled = nextValue;
                  }
                }}
              />
              {audioInputDeviceList.length > 1 && (
                <Pressable
                  hitSlop={{ top: 16, right: 16, bottom: 16, left: 16 }}
                  absoluteBottomRight
                  colorBackgroundWhite
                  centerContent
                  style={{ width: 20, height: 20, borderRadius: 10 }}
                  onPress={pickMicrophone}
                >
                  <Icon
                    name="chevron-down"
                    size={12}
                    color={styleConstants.colorTextDark}
                  />
                </Pressable>
              )}
            </View>
            {/* #endregion Toggle mic on/of button */}

            <Pressable
              style={({ pressed }) => [
                style.iconContainer,
                {
                  opacity: pressed ? 0.5 : 1,
                },
              ]}
              onPress={function pickAudioInput() {
                const audioOptions = getAudioOptions();

                const buttons: AlertButton[] = audioOptions.options.map(
                  (option) => {
                    const text =
                      option === "BLUETOOTH"
                        ? intl.formatMessage({
                            id: "cIA9Q3",
                            defaultMessage: "Bluetooth",
                            description:
                              "Option in the audio output picker. Should be the same in all languages as it is universaly recognized word for a type of audio output device.",
                          })
                        : option === "WIRED_HEADSET"
                          ? intl.formatMessage({
                              id: "zdbZVn",
                              defaultMessage: "Wired Headset",
                              description: "Option in the audio output picker",
                            })
                          : intl.formatMessage({
                              id: "vlLSqU",
                              defaultMessage: "Speaker",
                              description: "Option in the audio output picker",
                            });

                    return {
                      text,
                      onPress() {
                        setPreferredAudioOutput(option);
                      },
                    };
                  },
                );

                buttons.push({
                  text: intl.formatMessage({
                    id: "7tsY9l",
                    defaultMessage: "Cancel",
                    description: "Cancel button in alert",
                  }),
                  style: "cancel",
                  onPress: _.noop,
                });

                alert(
                  intl.formatMessage({
                    id: "O9P9NJ",
                    defaultMessage: "Audio output",
                    description: "Title of the audio output picker",
                  }),
                  undefined,
                  buttons,
                  undefined,
                  {
                    buttonsContainerStyle: {
                      flexDirection: "column",
                      height: buttons.length * 50,
                    },
                    cancelable: true,
                  },
                );
              }}
            >
              <Icon
                name="volume-high"
                size={24}
                color={styleConstants.colorTextLight}
              />

              <View
                absoluteBottomRight
                colorBackgroundWhite
                centerContent
                style={{ width: 20, height: 20, borderRadius: 10 }}
              >
                <Icon
                  name="chevron-down"
                  size={12}
                  color={styleConstants.colorTextDark}
                />
              </View>
            </Pressable>
          </View>
        )}
      </View>

      <Spacer extraSmall />
      {Platform.OS === "web" &&
        selectedVideoInputDevice &&
        selectedAudioInputDevice && (
          <View
            style={{
              alignSelf: "center",
              maxWidth: 600,
              width: "100%",
              gap: styleConstants.spacingMedium,
            }}
          >
            <Spacer medium />

            <View flexDirectionRow alignItemsCenter>
              <View style={{ width: 100 }}>
                <Text weightExtraBold>
                  {intl.formatMessage({
                    defaultMessage: "Camera:",
                    id: "clUsGm",
                    description: "Label for the camera picker",
                  })}
                </Text>
              </View>
              <Spacer medium />
              <Button
                outline
                small
                flex
                title={selectedVideoInputDevice.label}
                onPress={
                  videoInputDeviceList.length >= 3
                    ? async function pickCamera() {
                        // We already have a list of devices, but we enumerate them again
                        // in case the user has added a new device.
                        const deviceList: MediaDeviceInfo[] =
                          (await mediaDevices.enumerateDevices()) as any;
                        const videoDeviceList = deviceList.filter(
                          (device) => device.kind === "videoinput",
                        );

                        if (videoDeviceList.length === 0) {
                          alert("No video input devices found");
                          router.replace(getLocaleURL("/"));
                          return;
                        }

                        const device =
                          await new Promise<MediaDeviceInfo | null>(
                            (resolve) => {
                              alert(
                                "Camera",
                                undefined,
                                videoDeviceList.map((d) => {
                                  return {
                                    text: d.label,
                                    onPress: () => resolve(d),
                                  };
                                }),
                                undefined,
                                {
                                  cancelable: true,
                                  onDismiss: () => resolve(null),
                                },
                              );
                            },
                          );

                        if (!device) return;

                        setSelectedVideoInputDeviceId(device.deviceId);

                        const userMedia = await mediaDevices.getUserMedia({
                          ...(!selectedAudioInputDeviceId
                            ? undefined
                            : {
                                audio: { deviceId: selectedAudioInputDeviceId },
                              }),
                          video: { deviceId: device.deviceId },
                        });

                        // On Web, we check if `getSettings().facingMode is available, otherwise
                        // we fallback to the default "user" facing mode. This is because on
                        // laptops, the camera facingMode prop is not available, but we want to
                        // mirror the image since it's user facing.
                        for (const track of userMedia.getVideoTracks()) {
                          // @ts-expect-error This is reported as TS error, but exists on mobile
                          track.__facingMode =
                            track.getSettings().facingMode ?? "user";
                        }

                        getVideoDimensionsAndSetUserMedia(userMedia);
                      }
                    : async function switchCameraOnWeb() {
                        const deviceList: MediaDeviceInfo[] =
                          (await mediaDevices.enumerateDevices()) as any;

                        const videoDeviceList = deviceList.filter(
                          (device) => device.kind === "videoinput",
                        );

                        if (videoDeviceList.length === 0) {
                          alert("No video input devices found");
                          router.replace(getLocaleURL("/"));
                          return;
                        }

                        const currentIndex =
                          videoDeviceList.findIndex((device) => {
                            return (
                              device.deviceId === selectedVideoInputDeviceId
                            );
                          }) ?? -1;

                        const nextIndex =
                          (currentIndex + 1) % videoDeviceList.length;

                        const nextVideoDevice = videoDeviceList[nextIndex];

                        assert(!!nextVideoDevice, "No nextVideoDevice");

                        setSelectedVideoInputDeviceId(nextVideoDevice.deviceId);

                        const userMedia = await mediaDevices.getUserMedia({
                          ...(!selectedAudioInputDeviceId
                            ? undefined
                            : {
                                audio: {
                                  deviceId: selectedAudioInputDeviceId,
                                },
                              }),
                          video: { deviceId: nextVideoDevice.deviceId },
                        });

                        // On Web, we check if `getSettings().facingMode is available, otherwise
                        // we fallback to the default "user" facing mode. This is because on
                        // laptops, the camera facingMode prop is not available, but we want to
                        // mirror the image since it's user facing.
                        for (const track of userMedia.getVideoTracks()) {
                          // @ts-expect-error This is reported as TS error, but exists on mobile
                          track.__facingMode =
                            track.getSettings().facingMode ?? "user";
                        }

                        getVideoDimensionsAndSetUserMedia(userMedia);
                      }
                }
                style={{
                  justifyContent: "space-between",
                  flexDirection: "row-reverse",
                }}
              >
                <Icon
                  name="chevron-down"
                  color={styleConstants.colorTextTheme}
                  size={18}
                />
              </Button>
            </View>

            <View flexDirectionRow alignItemsCenter>
              <View style={{ width: 100 }}>
                <Text weightExtraBold>
                  {intl.formatMessage({
                    defaultMessage: "Microphone:",
                    id: "1m8dTZ",
                    description: "Label for the microphone picker",
                  })}
                </Text>
              </View>
              <Spacer medium />
              <Button
                outline
                small
                flex
                title={selectedAudioInputDevice.label}
                onPress={pickMicrophone}
                style={{
                  justifyContent: "space-between",
                  flexDirection: "row-reverse",
                }}
              >
                <Icon
                  name="chevron-down"
                  color={styleConstants.colorTextTheme}
                  size={18}
                />
              </Button>
            </View>
          </View>
        )}

      <Spacer extraLarge />

      <View
        flexDirectionRow
        style={{ maxWidth: 600, width: "100%", alignSelf: "center" }}
      >
        <Button
          style={{ flex: 1 }}
          large
          outline
          title={intl.formatMessage({
            defaultMessage: "Cancel",
            id: "iLcnSF",
            description: "LiveVideoSettingsScreen - Cancel",
          })}
          onPress={function goBack() {
            router.replace(getLocaleURL("/"));
          }}
        />
        <Spacer medium />
        <Button
          style={{ flex: 2 }}
          large
          title={intl.formatMessage({
            defaultMessage: "Let's Go!",
            id: "AEqk/a",
            description: "Let's Go! - text on the live video settings screen",
          })}
          onPress={() => {
            if (!userMediaWithDimensions) return;

            state.preferredAudioOutput = preferredAudioOutput;
            state.preferredIsLocalMicOn = isLocalMicOn;
            const videoTrackList =
              userMediaWithDimensions.userMedia.getVideoTracks();

            const videoTrack = videoTrackList.at(0);
            assert(!!videoTrack, "No videoTrack");

            state.preferredCameraFacingMode = facingMode;

            state.videoInputDeviceId = selectedVideoInputDeviceId;
            state.audioInputDeviceId = selectedAudioInputDeviceId;

            router.replace(getLocaleURL("/video-chat"));
          }}
        />
      </View>
    </View>
  );
});

const style = StyleSheet.create({
  localStream: {
    width: "100%",
    height: "100%",
  },
  iconContainer: {
    backgroundColor: styleConstants.colorBackgroundBackdrop,
    borderRadius: 29,
    height: 58,
    width: 58,
    justifyContent: "center",
    alignItems: "center",
  },
  micBadge: {
    position: "absolute",
    bottom: "auto",
    top: 3,
    right: 3,
    width: 14,
    height: 14,
    borderRadius: 7,
    pointerEvents: "none",
  },
});
